import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/leadership", label: "Leadership" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname, setOpen]);

  // Header is always on a light surface with navy content. Over the hero
  // (pre-scroll on Home) it stays mostly transparent but with a soft frosted
  // ivory veil + blur so the navigation links stay readable on top of the
  // bright daytime video.
  const overHeroLight = location.pathname === "/" && !scrolled;
  const headerBg = overHeroLight
    ? "bg-ivory/55 backdrop-blur-md border-b border-navy/5"
    : "bg-ivory/95 backdrop-blur-sm border-b border-navy/10 shadow-[0_1px_0_rgba(10,26,63,0.04)]";

  return (
    <header className="fixed top-0 left-0 right-0 z-40" data-testid="site-header">
      <div className={`transition-all duration-500 ${headerBg}`}>
        <div className="container-zt flex items-center justify-between h-24">
          <Link to="/" data-testid="header-logo-link" aria-label="ZT Global home">
            <Logo tone="light" />
          </Link>

          <nav className="hidden md:flex items-center gap-10" data-testid="primary-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[11px] uppercase tracking-[0.3em] transition-colors duration-300 ${
                    isActive ? "text-gold-600" : "text-navy/75 hover:text-gold-600"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="text-[11px] uppercase tracking-[0.3em] border border-navy/40 text-navy px-5 py-2.5 hover:bg-navy hover:text-ivory transition-colors duration-300"
              data-testid="nav-cta-inquire"
            >
              Inquire
            </Link>
          </nav>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-navy p-2 -mr-2"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <div
          className={`md:hidden overflow-hidden transition-[max-height] duration-500 ease-in-out bg-ivory border-t border-navy/10 ${
            open ? "max-h-96" : "max-h-0"
          }`}
          data-testid="mobile-menu"
        >
          <nav className="container-zt py-6 flex flex-col gap-5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.3em] ${isActive ? "text-gold-600" : "text-navy/75"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-2 inline-block text-[11px] uppercase tracking-[0.3em] border border-navy text-navy px-5 py-3 w-fit"
              data-testid="mobile-cta-inquire"
            >
              Inquire
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
